import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Back } from "../../../compoment/icon";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import Photo from "../../../assets/img/photo.png";
import Upload from "../../../assets/img/upload.png";
import PreviewPlaceholder from "../../../assets/img/fifa/placeholder-image.png";
import TakePhotoModal from "../../../modal/TakePhotoModal";
import { setUploadedImageUrl } from "../../../redux/slices/uploadFlowSlice";
import type { RootState } from "../../../redux/store";
import {
  clearFifaSelfie,
  isFifaWorldCup2026Station,
  persistFifaSelfie,
  readActiveStoredStation,
  readFifaSelfie,
  resolveFifaStationSlug,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import BackArrow from "../../../compoment/icon/BackArrow";

const UploadFifaImage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );
  const [photoModal, setPhotoModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);

  useEffect(() => {
    const stateImage = (location.state as { capturedImage?: string } | null)?.capturedImage?.trim();
    const resolvedImage = stateImage || uploadedImageUrl || readFifaSelfie() || null;

    setPreviewUrl(resolvedImage);

    if (resolvedImage && resolvedImage !== uploadedImageUrl) {
      dispatch(setUploadedImageUrl(resolvedImage));
    }
    if (resolvedImage) {
      persistFifaSelfie(resolvedImage);
    }
    // Only restore from navigation/redux/localStorage when route changes — not after local photo updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.key]);

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
    }
  }, [isFifaFlow, navigate, stationSlug]);

  const handlePhotoReady = (imageUrl: string) => {
    setPreviewUrl(imageUrl);
    dispatch(setUploadedImageUrl(imageUrl));
    persistFifaSelfie(imageUrl);
    setPhotoModal(false);
    navigate(`/${stationSlug}/upload-fifa-image`, {
      replace: true,
      state: { capturedImage: imageUrl, stationData },
    });
  };

  const handleProceed = () => {
    if (!previewUrl) return;
    dispatch(setUploadedImageUrl(previewUrl));
    persistFifaSelfie(previewUrl);
    navigate(`/${stationSlug}/enter-profile`, {
      state: {
        capturedImage: previewUrl,
        stationData,
      },
    });
  };

  const handleBack = () => {
    clearFifaSelfie();
    dispatch(setUploadedImageUrl(null));
    setPreviewUrl(null);
    navigate(`/${stationSlug}/select-fifa-card`);
  };

  if (!isFifaFlow) {
    return null;
  }

  return (
    <>
      <section
        className="uploadFifaImageSection"
        style={{ backgroundImage: `url(${FifaBg})` }}
      >
        <div className="uploadFifaImageInner">

          <div className="mosidaLogoWhite uploadFifaImageLogo mb-5">
            <img src={LOGO} alt="MOSIDA logo" className="logo" />
            <span className="badgeCreative">FIFA World Cup</span>
          </div>

          <button
            type="button"
            className="uploadFifaImageBack"
            onClick={handleBack}
          >
            <span className="me-1">
              <BackArrow />
            </span>
            Back
          </button>

          <div className="uploadFifaImageBody mt-5">
            <h1 className="uploadFifaImageHeading">
              <div className="uploadFifaImageHeadingLine">UPLOAD {" "}
                <span className="uploadFifaImageHeadingLine--gold">
                  YOUR IMAGE
                </span>
              </div>
            </h1>

            <div className="uploadFifaImagePreview">
              <img
                key={previewUrl || "placeholder"}
                src={previewUrl || PreviewPlaceholder}
                alt={previewUrl ? "Selected preview" : "Upload preview placeholder"}
                className={`uploadFifaImagePreviewPhoto${previewUrl ? " uploadFifaImagePreviewPhoto--user" : ""}`}
                draggable={false}
              />
            </div>

            <div className="uploadFifaImageActions mt-5">
              <button
                type="button"
                className="uploadFifaImageActionBtn"
                onClick={() => setPhotoModal(true)}
              >
                <span className="me-2">
                  <svg width="48" height="48" viewBox="0 0 67 67" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.33496 27.3831C8.33496 24.5377 10.6417 22.231 13.4872 22.231C15.4387 22.231 17.2227 21.1284 18.0954 19.3829L20.3765 14.8208C20.682 14.2097 20.8348 13.9041 21.008 13.6384C21.9119 12.2517 23.3869 11.3402 25.0314 11.1518C25.3465 11.1157 25.6882 11.1157 26.3714 11.1157H40.3171C41.0003 11.1157 41.3419 11.1157 41.6571 11.1518C43.3016 11.3402 44.7765 12.2517 45.6804 13.6384C45.8537 13.9041 46.0064 14.2097 46.312 14.8208L48.593 19.3829C49.4658 21.1284 51.2498 22.231 53.2013 22.231C56.0468 22.231 58.3535 24.5377 58.3535 27.3831V41.2856C58.3535 46.8565 58.3535 49.6419 57.0757 51.6755C56.4094 52.7359 55.5127 53.6326 54.4523 54.2989C52.4188 55.5766 49.6333 55.5766 44.0625 55.5766H22.626C17.0551 55.5766 14.2697 55.5766 12.2361 54.2989C11.1757 53.6326 10.279 52.7359 9.61273 51.6755C8.33496 49.6419 8.33496 46.8565 8.33496 41.2856V27.3831Z" stroke="white" strokeWidth="5.55761" />
                    <circle cx="33.3467" cy="36.1186" r="8.33642" stroke="white" strokeWidth="5.55761" />
                  </svg>
                </span>
                Take a photo
              </button>
              <button
                type="button"
                className="uploadFifaImageActionBtn"
                onClick={() =>
                  navigate(`/${stationSlug}/upload-image-qr-code`, {
                    state: { fifaUploadReturn: true },
                  })
                }
              >
                <span className="me-2">
                  <svg width="48" height="48" viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_14004_48389)">
                      <path d="M39.0681 43.4032H8.68173V13.0168H28.2158V8.67592H8.68173C6.29423 8.67592 4.34082 10.6293 4.34082 13.0168V43.4032C4.34082 45.7907 6.29423 47.7442 8.68173 47.7442H39.0681C41.4556 47.7442 43.409 45.7907 43.409 43.4032V23.8691H39.0681V43.4032ZM22.1603 36.5229L17.9062 31.4006L11.9374 39.0623H35.8124L28.129 28.8395L22.1603 36.5229ZM43.409 8.67592V2.16455H39.0681V8.67592H32.5568C32.5785 8.69763 32.5568 13.0168 32.5568 13.0168H39.0681V19.5065C39.0898 19.5282 43.409 19.5065 43.409 19.5065V13.0168H49.9204V8.67592H43.409Z" fill="white" />
                    </g>
                    <defs>
                      <clipPath id="clip0_14004_48389">
                        <rect width="52.091" height="52.091" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>

                </span>
                Upload photo
              </button>
            </div>

            <button
              type="button"
              className="uploadFifaImageProceedBtn mt-5"
              disabled={!previewUrl}
              onClick={handleProceed}
            >
              PROCEED
            </button>
          </div>
        </div>
      </section>

      {photoModal && (
        <TakePhotoModal
          show={photoModal}
          onHide={() => setPhotoModal(false)}
          onPhotoReady={handlePhotoReady}
        />
      )}
    </>
  );
};

export default UploadFifaImage;
